import React, { useState } from 'react';
import { useGameStore } from '../../stores/gameStore';
import { useUserStore } from '../../stores/userStore';
import { formatTime, formatNumber, calculateXpForLevel } from '../../shared/utils';

export function MainMenu() {
  const { startHostGame, setScreen, setModal } = useGameStore();
  const user = useUserStore(s => s.user);
  const wallet = useUserStore(s => s.wallet);
  const [playerName, setPlayerName] = useState(user?.displayName || 'Player');
  const [showCreateGame, setShowCreateGame] = useState(false);
  const [gameMode, setGameMode] = useState<import('../../shared/types').GameMode>('ffa');
  const [joinCode, setJoinCode] = useState('');

  const xpForNext = user ? calculateXpForLevel(user.level) : 1000;
  const xpProgress = user ? (user.xp / xpForNext) * 100 : 0;

  const gameModes = [
    { id: 'ffa' as const, name: 'Free For All', desc: 'Every player for themselves', icon: '⚔' },
    { id: 'team' as const, name: 'Team Deathmatch', desc: 'Teams fight for dominance', icon: '🛡' },
    { id: 'capture' as const, name: 'Capture Point', desc: 'Hold objectives to win', icon: '📡' },
    { id: 'infection' as const, name: 'Infection', desc: 'Survive the outbreak', icon: '☣' },
    { id: 'parkour' as const, name: 'Parkour', desc: 'Race through obstacles', icon: '🏃' },
    { id: 'survival' as const, name: 'Survival', desc: 'Last player standing', icon: '💀' },
    { id: 'sandbox' as const, name: 'Sandbox', desc: 'Create and experiment', icon: '🧱' },
  ];

  const handleCreateGame = () => {
    startHostGame(playerName, gameMode);
  };

  const handleJoinGame = () => {
    if (joinCode.trim()) {
      const { joinGame } = useGameStore.getState();
      joinGame(`wss://${window.location.host}/ws`, playerName, joinCode.trim());
    }
  };

  return (
    <div className="w-full h-full flex items-center justify-center relative">
      <div className="absolute inset-0 bg-gradient-to-br from-cf-black via-cf-graphite to-cf-black" />

      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cf-cyan via-cf-blue to-cf-purple" />

      <div className="absolute top-6 left-8 flex items-center gap-3 z-10">
        <div className="text-3xl font-black tracking-tighter">
          <span className="text-cf-cyan">CLOUD</span>
          <span className="text-cf-white">FLUNK</span>
        </div>
        <div className="px-2 py-0.5 bg-cf-cyan/20 border border-cf-cyan/30 rounded text-[10px] font-bold text-cf-cyan tracking-wider">
          BETA
        </div>
      </div>

      <div className="absolute top-6 right-8 flex items-center gap-4 z-10">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-cf-panel/60 backdrop-blur-sm border border-cf-border rounded-lg">
          <div className="w-3 h-3 rounded-full bg-cf-amber" />
          <span className="text-cf-amber font-bold text-sm">{formatNumber(wallet.balance)}</span>
          <span className="text-cf-muted text-xs">TR</span>
        </div>
        <button
          onClick={() => setScreen('shop')}
          className="px-3 py-1.5 bg-cf-panel/60 backdrop-blur-sm border border-cf-border rounded-lg text-cf-text text-sm hover:bg-cf-dark hover:border-cf-muted/30 transition-all"
        >
          Shop
        </button>
        <button
          onClick={() => setScreen('profile')}
          className="w-9 h-9 rounded-lg bg-cf-panel border border-cf-border overflow-hidden hover:border-cf-cyan/50 transition-all"
        >
          <div className="w-full h-full bg-gradient-to-br from-cf-cyan to-cf-blue flex items-center justify-center text-cf-black font-bold text-sm">
            {user?.displayName?.charAt(0) || 'P'}
          </div>
        </button>
      </div>

      <div className="relative z-10 flex flex-col items-center gap-8 max-w-4xl w-full px-6">
        <div className="text-center mb-4">
          <div className="text-7xl font-black tracking-tighter mb-2">
            <span className="text-cf-cyan">CLOUD</span>
            <span className="text-cf-white">FLUNK</span>
          </div>
          <p className="text-cf-muted text-sm tracking-wide">
            BROWSER FPS PLATFORM — CREATE · COMPETE · CONNECT
          </p>
        </div>

        <div className="w-full max-w-md space-y-3">
          <input
            type="text"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            placeholder="Enter your name..."
            className="cf-input text-center font-semibold text-lg py-3"
            maxLength={20}
          />

          {!showCreateGame ? (
            <div className="space-y-3">
              <button
                onClick={() => setShowCreateGame(true)}
                className="w-full cf-btn-primary py-4 text-base font-bold"
              >
                CREATE GAME
              </button>

              <div className="flex gap-3">
                <input
                  type="text"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value)}
                  placeholder="Enter room code..."
                  className="cf-input flex-1 text-center font-mono"
                  onKeyDown={(e) => e.key === 'Enter' && handleJoinGame()}
                />
                <button
                  onClick={handleJoinGame}
                  disabled={!joinCode.trim()}
                  className="cf-btn-secondary px-6"
                >
                  JOIN
                </button>
              </div>
            </div>
          ) : (
            <div className="cf-panel p-6 space-y-4 animate-scale-in">
              <div className="flex items-center justify-between">
                <h3 className="text-cf-white font-bold text-lg">Select Game Mode</h3>
                <button
                  onClick={() => setShowCreateGame(false)}
                  className="text-cf-muted hover:text-cf-white transition-colors text-xl"
                >
                  ✕
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {gameModes.map((mode) => (
                  <button
                    key={mode.id}
                    onClick={() => setGameMode(mode.id)}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      gameMode === mode.id
                        ? 'bg-cf-cyan/10 border-cf-cyan/50 text-cf-cyan'
                        : 'bg-cf-dark border-cf-border text-cf-text hover:border-cf-muted/50'
                    }`}
                  >
                    <div className="text-lg mb-1">{mode.icon}</div>
                    <div className="font-semibold text-sm">{mode.name}</div>
                    <div className="text-xs text-cf-muted mt-0.5">{mode.desc}</div>
                  </button>
                ))}
              </div>

              <button
                onClick={handleCreateGame}
                className="w-full cf-btn-primary py-3 text-base font-bold"
              >
                START MATCH
              </button>
            </div>
          )}
        </div>

        <div className="flex gap-4">
          {[
            { label: 'Leaderboard', screen: 'leaderboard' as const, icon: '🏆' },
            { label: 'Inventory', screen: 'inventory' as const, icon: '🎒' },
            { label: 'Editor', screen: 'editor' as const, icon: '🛠' },
            { label: 'Social', screen: 'social' as const, icon: '👥' },
            { label: 'Settings', screen: 'settings' as const, icon: '⚙' },
          ].map((item) => (
            <button
              key={item.screen}
              onClick={() => setScreen(item.screen)}
              className="flex flex-col items-center gap-1.5 px-4 py-3 rounded-xl bg-cf-panel/40 border border-cf-border/50 hover:bg-cf-panel hover:border-cf-muted/30 transition-all"
            >
              <span className="text-xl">{item.icon}</span>
              <span className="text-xs text-cf-muted font-medium">{item.label}</span>
            </button>
          ))}
        </div>

        {user && (
          <div className="flex items-center gap-6 text-xs text-cf-muted">
            <span>LVL {user.level}</span>
            <span>K/D: {user.stats.deaths > 0 ? (user.stats.kills / user.stats.deaths).toFixed(2) : user.stats.kills}</span>
            <span>{formatNumber(user.stats.gamesPlayed)} Games</span>
            <span>{formatNumber(user.stats.kills)} Kills</span>
          </div>
        )}
      </div>
    </div>
  );
}
